import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { invoicesService } from '../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const InvoicesTable = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadInvoices();
  }, [pagination.page, pagination.limit, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Status filtresi sadece 'all' değilse ekle
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      console.log('📋 loadInvoices called with params:', params);
      const response = await invoicesService.getAllInvoices(params);
      console.log('📋 invoicesService.getAllInvoices response:', response);

      if (response && response.success) {
        console.log('📋 Invoices response success:', response);
        console.log('📋 Invoices data:', response.data);
        console.log('📋 Invoices data length:', response.data?.length || 0);
        setInvoices(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        console.error('❌ Invoices error:', response);
        console.error('❌ Invoices error details:', {
          success: response?.success,
          error: response?.error,
          data: response?.data,
          fullResponse: response
        });
        toast.error(response?.error || 'Faturalar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Faturalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedInvoice || !newStatus) return;

    try {
      const response = await invoicesService.updateInvoiceStatus(selectedInvoice.id, newStatus);

      if (response.success) {
        toast.success('Fatura durumu güncellendi');
        setShowStatusModal(false);
        setSelectedInvoice(null);
        setNewStatus('');
        loadInvoices();
      } else {
        toast.error(response.error || 'Fatura durumu güncellenemedi');
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error('Fatura durumu güncellenirken bir hata oluştu');
    }
  };

  const openStatusModal = (invoice) => {
    setSelectedInvoice(invoice);
    setNewStatus(invoice.status);
    setShowStatusModal(true);
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Fatura #${invoice.id} silinecek. Emin misiniz?`)) {
      return;
    }

    try {
      const response = await invoicesService.deleteInvoice(invoice.id);

      if (response.success) {
        toast.success('Fatura silindi');
        loadInvoices();
      } else {
        toast.error(response.error || 'Fatura silinemedi');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Fatura silinirken bir hata oluştu');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        label: 'Beklemede', 
        bgColor: '#ffc107', 
        textColor: '#000',
        borderColor: '#ffc107'
      },
      paid: { 
        label: 'Ödendi', 
        bgColor: '#28a745', 
        textColor: '#fff',
        borderColor: '#28a745'
      },
      failed: { 
        label: 'Başarısız', 
        bgColor: '#dc3545', 
        textColor: '#fff',
        borderColor: '#dc3545'
      },
      void: { 
        label: 'İptal', 
        bgColor: '#6c757d', 
        textColor: '#fff',
        borderColor: '#6c757d'
      }
    };

    const config = statusConfig[status] || { 
      label: status, 
      bgColor: '#6c757d', 
      textColor: '#fff',
      borderColor: '#6c757d'
    };
    
    return (
      <span 
        className="badge" 
        style={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          border: `1px solid ${config.borderColor}`,
          padding: '6px 12px',
          borderRadius: '4px',
          fontWeight: '500',
          fontSize: '0.875rem'
        }}
      >
        {config.label}
      </span>
    );
  };

  const formatPrice = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div>
      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="pending">Beklemede</option>
            <option value="paid">Ödendi</option>
            <option value="failed">Başarısız</option>
            <option value="void">İptal</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>Organizasyon</th>
              <th>Organizasyon ID</th>
              <th>Plan</th>
              <th>Tutar</th>
              <th>Durum</th>
              <th>Dönem Başlangıç</th>
              <th>Dönem Bitiş</th>
              <th>Oluşturulma</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center">Fatura bulunamadı</td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.id}</td>
                  <td>{invoice.organization_name || '-'}</td>
                  <td>{invoice.organization_id}</td>
                  <td>{invoice.plan_name || '-'} {invoice.plan_interval ? `(${invoice.plan_interval})` : ''}</td>
                  <td>{formatPrice(invoice.amount, invoice.currency)}</td>
                  <td>{getStatusBadge(invoice.status)}</td>
                  <td>{formatDate(invoice.period_start)}</td>
                  <td>{formatDate(invoice.period_end)}</td>
                  <td>{formatDate(invoice.created_at)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openStatusModal(invoice)}
                        title="Durum Güncelle"
                      >
                        <Icon icon="mdi:pencil" />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteInvoice(invoice)}
                        title="Fatura Sil"
                      >
                        <Icon icon="mdi:delete" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Toplam {pagination.total} fatura, Sayfa {pagination.page} / {pagination.totalPages}
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Önceki
                </button>
              </li>
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Sonraki
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedInvoice && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Fatura Durumu Güncelle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedInvoice(null);
                    setNewStatus('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Fatura ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedInvoice.id}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Organizasyon</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedInvoice.organization_name || '-'}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Mevcut Durum</label>
                  <div>{getStatusBadge(selectedInvoice.status)}</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Yeni Durum</label>
                  <select
                    className="form-select"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="pending">Beklemede</option>
                    <option value="paid">Ödendi</option>
                    <option value="failed">Başarısız</option>
                    <option value="void">İptal</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedInvoice(null);
                    setNewStatus('');
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                >
                  Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesTable;

